import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const subcategories = [
  { value: "all", label: "Toutes" },
  { value: "plats_cuisines", label: "Plats cuisinés" },
  { value: "legumes", label: "Légumes" },
];

export default function Conserves() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?category=conserves`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filteredProducts = activeTab === "all" 
    ? products 
    : products.filter(p => p.subcategory === activeTab);

  return (
    <div className="py-12 md:py-16" data-testid="conserves-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900">
            Conserves
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Plats cuisinés, légumes en conserve et spécialités maison.
          </p>
        </div>

        {/* Tabs for subcategories */}
        <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent h-auto">
            {subcategories.map((sub) => (
              <TabsTrigger
                key={sub.value}
                value={sub.value}
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-2 rounded-full border"
                data-testid={`tab-${sub.value}`}
              >
                {sub.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="aspect-square" />
                <CardContent className="p-4">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3 mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              Aucun produit disponible dans cette catégorie. Revenez bientôt !
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Card
                key={product.id}
                className="overflow-hidden card-hover relative"
                data-testid={`product-${product.id}`}
              >
                {product.is_promotion && (
                  <span className="promo-badge">Promo</span>
                )}
                <div className="aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1547592180-85f173990554?w=400";
                    }}
                  />
                </div>
                <CardContent className="p-4">
                  <h3 className="font-heading text-lg font-semibold text-gray-900">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {product.description}
                  </p>
                  {product.price && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2">
                        {product.is_promotion && product.promotion_price ? (
                          <>
                            <span className="text-lg font-bold text-accent">
                              {product.promotion_price}
                            </span>
                            <span className="text-sm text-gray-400 line-through">
                              {product.price}
                            </span>
                          </>
                        ) : (
                          <span className="text-lg font-bold text-primary">
                            {product.price}
                          </span>
                        )}
                      </div>
                      {product.price_per_kg && (
                        <p className="text-xs text-gray-500 mt-1">
                          soit {product.price_per_kg}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
